/**
 * Validation utility functions for form validation
 */

import { isValidPhoneNumber, formatPhoneNumber } from '../../../utils/phoneUtils';

/**
 * Validates a South African phone number format
 * 
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Whether the phone number is valid
 */
export const isValidSaPhone = (phone) => {
  // Format must be one of:
  // - +27XXXXXXXXX (12 chars)
  // - 0XXXXXXXXX (10 chars)
  
  // Remove any spaces
  const cleanedPhone = phone.replace(/\s+/g, '');
  
  // Check +27 format
  if (cleanedPhone.startsWith('+27')) {
    return /^\+27\d{9}$/.test(cleanedPhone);
  }
  
  // Check 0 format
  if (cleanedPhone.startsWith('0')) {
    return /^0\d{9}$/.test(cleanedPhone);
  }
  
  return false;
};

/**
 * Validates a South African ID number using Luhn algorithm
 * 
 * @param {string} id - ID number to validate
 * @returns {boolean} Whether the ID number is valid
 */
export const isValidSaId = (id) => {
  // Remove any spaces
  const cleanedId = id.replace(/\s+/g, '');
  
  // Must be 13 digits
  if (!/^\d{13}$/.test(cleanedId)) {
    return false;
  }
  
  // Extract date components
  const year = parseInt(cleanedId.substring(0, 2));
  const month = parseInt(cleanedId.substring(2, 4));
  const day = parseInt(cleanedId.substring(4, 6));
  
  // Validate date
  const fullYear = year < 50 ? 2000 + year : 1900 + year;
  const date = new Date(fullYear, month - 1, day);
  if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  
  // Luhn algorithm validation
  let sum = 0;
  let isEven = false;
  
  // Loop through values starting from the rightmost digit
  for (let i = cleanedId.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanedId.charAt(i));
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

/**
 * Validates an email address
 * 
 * @param {string} email - Email address to validate
 * @returns {boolean} Whether the email address is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Validates Step 1: Customer Details
 * 
 * @param {Object} form - Form data
 * @param {string} customerType - Customer type ('home' or 'business')
 * @param {Object} intlTel - International telephone state
 * @param {Object} nonRsaId - Non-RSA ID state
 * @param {Object} verified - OTP verification state
 * @param {Object} files - Uploaded files
 * @param {string} rentStatus - Rent status
 * @returns {Object} Object containing validation results
 */
export const validateStep1 = (
  form,
  customerType,
  intlTel,
  nonRsaId,
  verified,
  files,
  rentStatus
) => {
  const errors = {};
  const fileErrors = {};
  
  // Common fields
  if (!form.installation_address) {
    errors.installation_address = 'Installation address is required';
  }
  
  if (!form.gps_coordinates) {
    errors.gps_coordinates = 'Please select a location on the map';
  }
  
  // Customer type specific fields
  if (customerType === 'home') {
    // Home customer validation
    if (!form.customer_full_names) {
      errors.customer_full_names = 'Full names are required';
    }
    
    if (!form.customer_surname) {
      errors.customer_surname = 'Surname is required';
    }
    
    if (!form.rsa_id_number) {
      errors.rsa_id_number = 'ID number is required';
    } else if (!nonRsaId.home && !isValidSaId(form.rsa_id_number)) {
      errors.rsa_id_number = 'Invalid ID number format';
    }
    
    if (nonRsaId.home && !form.home_country_of_issue) {
      errors.home_country_of_issue = 'Country of issue is required';
    }
    
    if (!form.primary_telephone) {
      errors.primary_telephone = 'Primary telephone is required';
    } else if (!intlTel.primary_telephone && !isValidPhoneNumber(form.primary_telephone)) {
      errors.primary_telephone = 'Invalid South African phone number';
    }
    
    if (!form.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(form.email)) {
      errors.email = 'Invalid email format';
    } else if (!verified.email) {
      errors.email = 'Email must be verified';
    }
    
    if (form.whatsapp && !isValidPhoneNumber(form.whatsapp)) {
      errors.whatsapp = 'Invalid WhatsApp number';
    } else if (form.whatsapp && !verified.whatsapp) {
      errors.whatsapp = 'WhatsApp number must be verified';
    }
    
    // Landlord information for renters
    if (rentStatus === 'rent') {
      if (!form.landlord_name) {
        errors.landlord_name = 'Landlord name is required';
      }
      
      if (!form.landlord_telephone) {
        errors.landlord_telephone = 'Landlord telephone is required';
      } else if (!intlTel.landlord_telephone && !isValidPhoneNumber(form.landlord_telephone)) {
        errors.landlord_telephone = 'Invalid South African phone number';
      }
      
      if (form.landlord_email && !isValidEmail(form.landlord_email)) {
        errors.landlord_email = 'Invalid email format';
      }
      
      // Rental agreement document
      if (!files.rental_agreement) {
        fileErrors.rental_agreement = 'Rental agreement is required';
      }
    }
    
    // ID Document
    if (!files.id_document) {
      fileErrors.id_document = 'ID document is required';
    }
  } else {
    // Business customer validation
    if (!form.company_name) {
      errors.company_name = 'Company name is required';
    }
    
    if (!form.company_reg_number) {
      errors.company_reg_number = 'Company registration number is required';
    }
    
    if (!form.primary_director_full_names) {
      errors.primary_director_full_names = 'Director full names are required';
    }
    
    if (!form.primary_director_surname) {
      errors.primary_director_surname = 'Director surname is required';
    }
    
    if (!form.primary_director_id_number) {
      errors.primary_director_id_number = 'Director ID number is required';
    } else if (!nonRsaId.business && !isValidSaId(form.primary_director_id_number)) {
      errors.primary_director_id_number = 'Invalid ID number format';
    }
    
    if (nonRsaId.business && !form.business_country_of_issue) {
      errors.business_country_of_issue = 'Country of issue is required';
    }
    
    if (!form.accounts_contact_person) {
      errors.accounts_contact_person = 'Accounts contact person is required';
    }
    
    if (!form.accounts_telephone) {
      errors.accounts_telephone = 'Accounts telephone is required';
    } else if (!intlTel.accounts_telephone && !isValidPhoneNumber(form.accounts_telephone)) {
      errors.accounts_telephone = 'Invalid South African phone number';
    }
    
    if (!form.accounts_email) {
      errors.accounts_email = 'Accounts email is required';
    } else if (!isValidEmail(form.accounts_email)) {
      errors.accounts_email = 'Invalid email format';
    } else if (!verified.accounts_email) {
      errors.accounts_email = 'Email must be verified';
    }
    
    if (form.accounts_whatsapp && !isValidPhoneNumber(form.accounts_whatsapp)) {
      errors.accounts_whatsapp = 'Invalid WhatsApp number';
    } else if (form.accounts_whatsapp && !verified.accounts_whatsapp) {
      errors.accounts_whatsapp = 'WhatsApp number must be verified';
    }
    
    // Company documents
    if (!files.company_registration) {
      fileErrors.company_registration = 'Company registration document is required';
    }
    
    if (!files.director_id) {
      fileErrors.director_id = 'Director ID document is required';
    }
  }
  
  return { errors, fileErrors };
};

/**
 * Validates a contact form
 * 
 * @param {Object} contact - Contact form data
 * @returns {Object} Validation errors
 */
export const validateContactForm = (contact) => {
  const errors = {};
  
  // Name is required
  if (!contact.name) {
    errors.name = 'Contact name is required';
  }
  
  // Must have at least one contact method
  if (!contact.telephone && !contact.email && !contact.whatsapp) {
    errors.general = 'At least one contact method (telephone, email, or WhatsApp) is required';
  }
  
  // Validate phone format
  if (contact.telephone && !isValidSaPhone(contact.telephone)) {
    errors.telephone = 'Invalid phone number format';
  }
  
  // Validate email format
  if (contact.email && !isValidEmail(contact.email)) {
    errors.email = 'Invalid email format';
  }
  
  // Validate WhatsApp format
  if (contact.whatsapp && !isValidSaPhone(contact.whatsapp)) {
    errors.whatsapp = 'Invalid WhatsApp number format';
  }
  
  return errors;
};

/**
 * Validates Step 3: Service Setup
 * 
 * @param {string} internetType - Internet connection type
 * @param {string|number} selectedPackage - Selected package ID
 * @param {Array} selectedEquipment - Selected equipment IDs
 * @param {Array} closestTowers - Closest towers data
 * @returns {Object} Object containing validation results
 */
export const validateServiceSetup = (
  internetType,
  selectedPackage,
  selectedEquipment,
  closestTowers
) => {
  // Check if internet type is selected
  if (!internetType) {
    return {
      isValid: false,
      error: 'Please select an internet connection type'
    };
  }
  
  // Check if a package is selected
  if (!selectedPackage) {
    return {
      isValid: false,
      error: 'Please select a service package'
    };
  }
  
  // For wireless connection, check if there are nearby towers
  if (internetType === 'wireless' && (!closestTowers || closestTowers.length === 0)) {
    return {
      isValid: false,
      error: 'No towers found in your area. Please choose a different internet type.'
    };
  }
  
  // Check if equipment is selected
  if (!selectedEquipment || selectedEquipment.length === 0) {
    return {
      isValid: false,
      error: 'Please select equipment for your installation'
    };
  }
  
  // All validations passed
  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validates Step 4: Debit Order
 * 
 * @param {Object} form - Debit order form data
 * @param {string} signature - Signature data URL
 * @returns {Object} Object containing validation errors and isValid flag
 */
export const validateDebitOrderForm = (form, signature) => {
  const errors = {};
  
  if (!form.account_holder_name) {
    errors.account_holder_name = 'Account holder name is required';
  }
  
  if (!form.debit_order_id_number) {
    errors.debit_order_id_number = 'ID number / Company Registration is required';
  }
  
  if (!form.debit_order_telephone) {
    errors.debit_order_telephone = 'Telephone number is required';
  } else if (!isValidSaPhone(form.debit_order_telephone)) {
    errors.debit_order_telephone = 'Invalid South African phone number format';
  }
  
  if (!form.installation_address) {
    errors.installation_address = 'Physical address is required';
  }
  
  if (!form.bank_name) {
    errors.bank_name = 'Bank name is required';
  } else if (form.bank_name === 'Other' && !form.other_bank_name) {
    errors.other_bank_name = 'Please specify other bank name';
  }
  
  if (!form.branch_name) {
    errors.branch_name = 'Branch name is required';
  }
  
  if (!form.branch_code) {
    errors.branch_code = 'Branch code is required';
  }
  
  if (!form.account_number) {
    errors.account_number = 'Account number is required';
  }
  
  if (!form.account_type) {
    errors.account_type = 'Account type is required';
  }
  
  if (!form.first_payment_date) {
    errors.first_payment_date = 'First payment date is required';
  }
  
  if (!form.debit_order_day) {
    errors.debit_order_day = 'Debit order day is required';
  }

  if (!signature) { // Check if signature is null, undefined, or empty string
    errors.signature = 'Signature is required. Please sign in the designated area.';
  }

  if (!form.date_signed) {
    errors.date_signed = 'Date signed is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Handles signature drawing start
 * @param {Object} e - The event object
 * @param {Object} canvas - The canvas element
 * @param {Object} ctx - The canvas 2D context
 */
export const startDrawing = (e, canvas, ctx) => {
  if (!canvas || !ctx) return;
  
  const rect = canvas.getBoundingClientRect();
  const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
  const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
  const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
  const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
  const x = (clientX - rect.left) * scaleX;
  const y = (clientY - rect.top) * scaleY;
  
  ctx.beginPath();
  ctx.moveTo(x, y);
};

/**
 * Handles signature drawing
 * @param {Object} e - The event object
 * @param {Object} canvas - The canvas element
 * @param {Object} ctx - The canvas 2D context
 */
export const draw = (e, canvas, ctx) => {
  if (!canvas || !ctx) return;
  
  const rect = canvas.getBoundingClientRect();
  const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
  const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
  const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
  const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
  const x = (clientX - rect.left) * scaleX;
  const y = (clientY - rect.top) * scaleY;
  
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'black';
  
  ctx.lineTo(x, y);
  ctx.stroke();
};

/**
 * Handles signature drawing end
 * @param {Object} canvas - The canvas element
 */
export const stopDrawing = (canvas) => {
  if (!canvas) {
    console.error("Canvas is null when trying to stop drawing");
    return;
  }
};

/**
 * Clears the signature canvas
 * @param {Object} canvas - The canvas element
 */
export const clearSignature = (canvas) => {
  if (!canvas) {
    console.error("Canvas is null when trying to clear signature");
    return;
  }
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error("Canvas context is null");
    return;
  }
  
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

/**
 * Generate a unique customer code based on name/company
 * Format: 3 capital letters (from name) + 3 numbers (incremental)
 * Example: AAA012
 * 
 * @param {string} name - Customer surname or company name
 * @param {Object} api - API client instance
 * @returns {Promise<string>} Generated customer code
 */
export const generateCustomerCode = async (name, api) => {
  try {
    // Get first 3 letters of name, convert to uppercase
    const prefix = name.substring(0, 3).toUpperCase();
    
    // Search for existing codes with this prefix using case-insensitive LIKE
    const response = await api.post('/db/customers/filter', {
      filters: {
        code__like: `${prefix}%`
      },
      columns: ['code']
    });
    
    // Get all existing codes with this prefix
    const existingCodes = Array.isArray(response) ? response : (response.data || []);
    
    // Find the highest number used
    let maxNumber = 0;
    existingCodes.forEach(customer => {
      if (customer.code) {
        const numberPart = parseInt(customer.code.substring(3));
        if (!isNaN(numberPart) && numberPart > maxNumber) {
          maxNumber = numberPart;
        }
      }
    });
    
    // Generate new code with incremented number
    let newNumber = maxNumber + 1;
    let newCode = `${prefix}${newNumber.toString().padStart(3, '0')}`;
    
    // Check if this exact code exists (in case of gaps in numbering)
    let isUnique = false;
    while (!isUnique) {
      const checkResponse = await api.post('/db/customers/filter', {
        filters: {
          code: newCode
        },
        limit: 1
      });
      
      const existing = Array.isArray(checkResponse) ? checkResponse : (checkResponse.data || []);
      
      if (existing.length === 0) {
        isUnique = true;
      } else {
        newNumber++;
        newCode = `${prefix}${newNumber.toString().padStart(3, '0')}`;
      }
    }
    
    return newCode;
  } catch (error) {
    console.error('Error generating customer code:', error);
    throw error;
  }
}; 